import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import './styles/design-tokens.css';
import { useEffect, useState } from 'react';
import axios from 'axios';
import ReactGA from 'react-ga4';
import Lenis from 'lenis';
import { X, LogIn } from 'lucide-react';
import API_BASE_URL from './config/api';

// Layout Components
import Navbar from './layout/Navbar';
import Footer from './layout/Footer';

// Pages
import Home from './pages/Home';
import NoticeBoard from './pages/NoticeBoard';
import Gallery from './pages/Gallery';
import Academics from './pages/Academics';
import Admissions from './pages/Admissions';
import Contact from './pages/Contact';
import PDFViewer from './pages/PDFViewer';
import AdminDashboard from './pages/AdminDashboard';
import AdmissionForm from './pages/AdmissionForm';
import Calendar from './pages/Calendar';

const TRACKING_ID = 'G-YJTBTHK92Z';
ReactGA.initialize(TRACKING_ID);

function AnalyticsTracker() {
  const location = useLocation();
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: location.pathname });
  }, [location]);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppContent({ 
  IsLoggedIn, handleLogout, showLoginModal, setShowLoginModal, 
  handleLogin, username, setUsername, password, setPassword 
}) {
  const location = useLocation();
  const isAdminPage = location.pathname.toLowerCase() === '/admin';

  return (
    <>
      <AnalyticsTracker />
      <ScrollToTop />
      
      {!isAdminPage && <Navbar IsLoggedIn={IsLoggedIn} onLogout={handleLogout} />}
      
      {/* Hidden button to trigger login modal from navbar */}
      <button 
        id="trigger-login-modal" 
        style={{ display: 'none' }} 
        onClick={() => setShowLoginModal(true)} 
      />

      <div style={{ flexGrow: 1 }}>
        <Routes>
          <Route path='/' exact element={<Home />} />
          <Route path='/Notice' exact element={<NoticeBoard />} />
          <Route path='/Gallery' exact element={<Gallery />} />
          <Route path='/Academics' exact element={<Academics />} />
          <Route path='/Admissions' exact element={<Admissions />} />
          <Route path='/Contact' exact element={<Contact />} />
          <Route path='/Documentation' exact element={<PDFViewer />} />
          <Route path='/Admin' exact element={<AdminDashboard />} />
          <Route path='/Apply' exact element={<AdmissionForm />} />
          <Route path='/Calendar' exact element={<Calendar />} />
        </Routes>
      </div>

      {!isAdminPage && <Footer />}

      {/* Premium Login Modal */}
      {showLoginModal && (
        <div style={modalOverlayStyle} onClick={() => setShowLoginModal(false)}>
          <div style={modalCardStyle} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--primary-color)' }}>Portal Administration</h2>
              <button onClick={() => setShowLoginModal(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Username</label>
                <input 
                  type="text" 
                  value={username} 
                  onChange={(e) => setUsername(e.target.value)} 
                  className="form-input" 
                  placeholder="Enter Admin Username" 
                  required 
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Password</label>
                <input 
                  type="password" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  className="form-input" 
                  placeholder="••••••••" 
                  required 
                />
              </div>
              <button type="submit" className="btn-premium btn-premium-primary" style={{ width: '100%', marginTop: '10px' }}>
                <LogIn size={16} /> Authenticate Admin
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

function App() {
  const [IsLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smooth: true,
      smoothTouch: false,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => lenis.destroy();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    axios.post(`${API_BASE_URL}/Login`, { username, password })
      .then(res => {
        if (res.data.token) {
          localStorage.setItem('token', res.data.token);
          setIsLoggedIn(true);
          setShowLoginModal(false);
          alert('Login Successful');
          window.location.reload();
        } else {
          alert(typeof res.data === 'string' ? res.data : 'User does not exist or login error');
        }
      })
      .catch(error => {
        console.log(error);
        alert('Server login request failed');
      });
  };

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      localStorage.removeItem('token');
      setIsLoggedIn(false);
      window.location.reload();
    }
  };

  return (
    <div 
      className="App" 
      style={{ 
        backgroundColor: '#FFFDF7',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Router>
        <AppContent 
          IsLoggedIn={IsLoggedIn} 
          handleLogout={handleLogout} 
          showLoginModal={showLoginModal} 
          setShowLoginModal={setShowLoginModal} 
          handleLogin={handleLogin}
          username={username}
          setUsername={setUsername}
          password={password}
          setPassword={setPassword}
        />
      </Router>
    </div>
  );
}

// Modal overlay style
const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(8px)',
  zIndex: 10000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const modalCardStyle = {
  backgroundColor: '#ffffff',
  padding: '32px',
  borderRadius: '20px',
  width: '90%',
  maxWidth: '400px',
  boxShadow: 'var(--shadow-xl)',
  border: '1px solid var(--border-color)'
};

export default App;
